import { Box, Button, MenuItem, Select, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import type { IntegrationProviderConfig } from '../types';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import Label from '../../UI/Label/Label';
import { ThirdPartyIntegrationService } from '../../../service/ThirdPartyIntegrationService';

interface PullModePanelProps {
  config: IntegrationProviderConfig;
  formValues: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  onFieldFocus?: (name: string) => void;
  onFieldBlur?: () => void;
  nodeId: string;
  provider: string;
  disabled?: boolean;
  connected?: boolean;
  nodeData?: Record<string, unknown>;
  errorFieldName?: string | null;
}

interface LastReceivedState {
  payload: unknown;
  lastReceivedAt: string | null;
  loading: boolean;
  error: string | null;
}

const PullModePanel: React.FC<PullModePanelProps> = ({
  config,
  formValues,
  onChange,
  onFieldFocus,
  onFieldBlur,
  nodeId,
  provider,
  disabled,
  connected,
  nodeData,
  errorFieldName,
}) => {
  const { user } = useAuthStore(state => state);
  const { setNotification } = useNotificationStore(state => state);
  const [lastReceived, setLastReceived] = useState<LastReceivedState>({
    payload: null,
    lastReceivedAt: null,
    loading: false,
    error: null,
  });

  const pullConfig = config.pullConfig;
  if (!pullConfig) return null;

  const selectedObject = (formValues.pullObjectType as string) ?? pullConfig.objectTypes[0]?.value;
  const fields = pullConfig.getFields(selectedObject);

  const apiBase = (import.meta as unknown as { env: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? '';
  const resolvedValues = {
    ...formValues,
    ...(pullConfig.resolveReadOnlyValues?.({
      nodeId,
      apiBase,
      selectedObject,
    }) ?? {}),
  };

  const handleLoadLastReceived = useCallback(async () => {
    setLastReceived(prev => ({ ...prev, loading: true, error: null }));
    try {
      const res = await ThirdPartyIntegrationService.pullData<{
        payload: unknown;
        lastReceivedAt: string | null;
      }>(provider, {
        pullObjectType: selectedObject,
        nodeId,
        ...(nodeData?.integrationId ? { integrationId: nodeData.integrationId } : {}),
        ...(nodeData?.connectedByUserId ? { connectedByUserId: nodeData.connectedByUserId } : {}),
      });
      const data = res?.data;
      setLastReceived({
        payload: data?.payload ?? null,
        lastReceivedAt: data?.lastReceivedAt ?? null,
        loading: false,
        error: null,
      });
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to load';
      setLastReceived(prev => ({
        ...prev,
        loading: false,
        error: message,
      }));
    }
  }, [provider, selectedObject, nodeId, nodeData?.integrationId, nodeData?.connectedByUserId]);

  const helpCaption = pullConfig.objectTypeHelpCaption?.(selectedObject);
  const showLoadLastReceived =
    typeof pullConfig.supportsLoadLastReceived === 'function'
      ? pullConfig.supportsLoadLastReceived(selectedObject)
      : !!pullConfig.supportsLoadLastReceived;
  const isDark = user?.theme === 'dark';
  const PullPanelExtras = pullConfig.PullPanelExtras;

  const selectSx = {
    width: '100%',
    maxWidth: '360px',
    '& .MuiList-root': {
      backgroundColor: isDark ? '#101828' : '#F9FAFB',
    },
    borderRadius: '8px',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
    bgcolor: isDark ? '#101828' : '#F9FAFB',
    color: isDark ? '#D0D5DD' : '#101828',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box>
        <Label
          labelName="Object"
          isAdvanced
          labelStyle={{
            fontSize: '14px',
            color: isDark ? '#D0D5DD' : '#101828',
            fontWeight: 400,
          }}
        />
        <Select
          className="nodrag"
          value={selectedObject}
          onChange={e => onChange('pullObjectType', e.target.value)}
          onFocus={() => onFieldFocus?.('pullObjectType')}
          onBlur={onFieldBlur}
          disabled={disabled}
          sx={selectSx}
          size="small"
        >
          {pullConfig.objectTypes.map(opt => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{ color: isDark ? '#D0D5DD' : 'black' }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {helpCaption != null && (
        <Typography variant="caption" sx={{ color: isDark ? '#98A2B3' : '#667085', mt: -1 }}>
          {helpCaption}
        </Typography>
      )}

      <DynamicFieldRenderer
        fields={fields}
        values={resolvedValues}
        onChange={onChange}
        onFieldFocus={onFieldFocus}
        onFieldBlur={onFieldBlur}
        nodeId={nodeId}
        provider={provider}
        context={{ objectType: selectedObject }}
        disabled={disabled}
        connected={connected}
        nodeData={nodeData}
        errorFieldName={errorFieldName}
      />

      {PullPanelExtras && (
        <PullPanelExtras
          provider={provider}
          config={config}
          formValues={formValues}
          resolvedValues={resolvedValues}
          selectedObject={selectedObject}
          nodeId={nodeId}
          nodeData={nodeData}
          disabled={disabled}
          isDark={isDark}
          lastReceived={lastReceived}
          onLoadLastReceived={handleLoadLastReceived}
          setNotification={setNotification}
          onChange={onChange}
        />
      )}

      {showLoadLastReceived && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
          <Label
            labelName="Last Received"
            isAdvanced
            labelStyle={{
              fontSize: '14px',
              color: isDark ? '#D0D5DD' : '#101828',
              fontWeight: 400,
            }}
          />
          <Button
            className="nodrag"
            size="small"
            variant="outlined"
            onClick={handleLoadLastReceived}
            disabled={disabled || lastReceived.loading}
          >
            {lastReceived.loading ? 'Loading...' : 'Load last received'}
          </Button>
          {lastReceived.error && (
            <Typography variant="caption" color="error">
              {lastReceived.error}
            </Typography>
          )}
          {(lastReceived.payload != null || lastReceived.lastReceivedAt != null) && (
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: isDark ? '#1a1a1a' : '#f5f5f5',
                maxHeight: 160,
                overflow: 'auto',
              }}
            >
              {lastReceived.lastReceivedAt && (
                <Typography variant="caption" component="div" sx={{ mb: 0.5, opacity: 0.8 }}>
                  {new Date(lastReceived.lastReceivedAt).toLocaleString()}
                </Typography>
              )}
              <Typography
                component="pre"
                variant="caption"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                }}
              >
                {lastReceived.payload != null
                  ? typeof lastReceived.payload === 'string'
                    ? lastReceived.payload
                    : JSON.stringify(lastReceived.payload, null, 2)
                  : 'No payload yet'}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default PullModePanel;
